import { useEffect, useState } from 'react';
import { Input, Button, Alert } from '@dreamtree-org/twreact-ui';
import { Truck, MapPin } from 'lucide-react';
import { useMutation } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { isValidPin } from '../lib/cart';
import { useStorefront } from '../StorefrontContext';

/**
 * PIN-code serviceability (WEB-03/05): delivery estimate and shipping for a PIN.
 * Remembers the last PIN so the next page can re-use it.
 */
export default function PinCheck({ onResult, compact = false }) {
  const sf = useStorefront();
  const check = useMutation('sfPinCheck');
  const [pin, setPin] = useState(sf.pin || '');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async (value) => {
    const p = String(value || '').trim();
    if (!isValidPin(p)) {
      setError('Enter a valid 6-digit PIN code, for example 560038.');
      setResult(null);
      return;
    }
    setError('');
    try {
      const r = await check.run({ pin: p });
      setResult(r.data);
      sf.setPin(p);
      if (onResult) onResult(r.data);
    } catch {
      setResult(null);
    }
  };

  useEffect(() => {
    if (sf.pin) run(sf.pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3">
      <form
        className="flex items-start gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          run(pin);
        }}
      >
        <div className="flex-1">
          <Input
            type="tel"
            inputMode="numeric"
            maxLength={6}
            aria-label="Delivery PIN code"
            placeholder="Enter PIN code"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            error={error || undefined}
            leftIcon={<MapPin className="h-4 w-4" />} />
        </div>
        <Button type="submit" variant="outline" loading={check.loading}>Check</Button>
      </form>
      {check.error ? <Alert variant="error">{check.error}</Alert> : null}
      {result && !result.serviceable ? <Alert variant="warning" title={`Not deliverable to ${result.pin}`}>{result.message}</Alert> : null}
      {result && result.serviceable ? (
        <div className="flex items-start gap-3 text-sm">
          <Truck className="mt-0.5 h-5 w-5 shrink-0 text-success-600" aria-hidden="true" />
          <div>
            <div className="font-semibold">Delivers to {result.pin} ({result.city})</div>
            <div className="opacity-80">Free delivery by {formatDate(result.eta_date)} ({result.eta_min_days}–{result.eta_max_days} days)</div>
            {!compact && result.express_available ? <div className="opacity-80">Express next-day delivery available at checkout</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
